import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useWS } from '../hooks/useWebSocket';

function SeverityBadge({ severity }) {
  const map = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-blue-100 text-blue-800',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[severity] || map.medium}`}>{severity}</span>;
}

function StatusBadge({ status }) {
  const map = {
    pending: 'bg-red-50 text-red-700',
    acknowledged: 'bg-amber-50 text-amber-700',
    resolved: 'bg-emerald-50 text-emerald-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || ''}`}>{status}</span>;
}

import { formatDateTime as formatDate } from '../utils/dateFormat';

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [counts, setCounts] = useState({ pending: 0, acknowledged: 0, resolved: 0 });
  const [clearing, setClearing] = useState(false);

  // Fetch summary counts for filter badges
  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard/stats');
      const a = data.stats?.alerts || {};
      setCounts({
        pending: a.pending || 0,
        acknowledged: a.acknowledged || 0,
        resolved: a.resolvedToday || 0,
      });
    } catch {}
  }, []);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      params.sortBy = 'created_at';
      params.order = 'desc';
      const { data } = await api.get('/alerts', { params });
      setAlerts(data.data);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      setError('Failed to load alerts. Please try again.');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  useWS(
    ['new_alert', 'alert_acknowledged', 'alert_resolved', 'alerts_bulk_resolved'],
    useCallback(() => { fetchAlerts(); fetchCounts(); }, [fetchAlerts, fetchCounts])
  );

  const handleAcknowledge = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/acknowledge`);
      fetchAlerts();
      fetchCounts();
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/resolve`);
      fetchAlerts();
      fetchCounts();
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    }
  };

  // Bulk "clear past notifications": resolve every acknowledged (already-seen)
  // alert in scope at once. Pending alerts stay; emergency/critical are kept by
  // the backend regardless. Mirrors the single-resolve flow, just batched.
  const handleClearSeen = async () => {
    if (!window.confirm(
      'Resolve all acknowledged alerts? Pending, emergency, and critical alerts are kept.'
    )) return;
    setClearing(true);
    try {
      await api.post('/alerts/clear');
      fetchAlerts();
      fetchCounts();
    } catch (err) {
      console.error('Failed to clear alerts:', err);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Alert Management</h1>
          <p className="text-secondary-500 text-sm mt-1">Review, acknowledge, and resolve device alerts</p>
        </div>
        <button
          onClick={handleClearSeen}
          disabled={clearing || counts.acknowledged === 0}
          title="Resolve all acknowledged alerts (emergency & critical are kept)"
          className="self-start sm:self-auto px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {clearing ? 'Clearing…' : 'Clear acknowledged'}
        </button>
      </div>

      {/* Summary counts */}
      <div className="grid grid-cols-3 gap-3">
        <button onClick={() => { setStatusFilter('pending'); setPage(1); }}
          className={`p-3 rounded-xl text-center transition ${statusFilter === 'pending' ? 'ring-2 ring-red-300' : ''} bg-red-50`}>
          <p className="text-2xl font-bold text-red-700">{counts.pending}</p>
          <p className="text-xs text-red-600 font-medium">Pending</p>
        </button>
        <button onClick={() => { setStatusFilter('acknowledged'); setPage(1); }}
          className={`p-3 rounded-xl text-center transition ${statusFilter === 'acknowledged' ? 'ring-2 ring-amber-300' : ''} bg-amber-50`}>
          <p className="text-2xl font-bold text-amber-700">{counts.acknowledged}</p>
          <p className="text-xs text-amber-600 font-medium">Acknowledged</p>
        </button>
        <button onClick={() => { setStatusFilter('resolved'); setPage(1); }}
          className={`p-3 rounded-xl text-center transition ${statusFilter === 'resolved' ? 'ring-2 ring-emerald-300' : ''} bg-emerald-50`}>
          <p className="text-2xl font-bold text-emerald-700">{counts.resolved}</p>
          <p className="text-xs text-emerald-600 font-medium">Resolved Today</p>
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex gap-2">
        {['', 'pending', 'acknowledged', 'resolved'].map((s) => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-lg border transition ${
              statusFilter === s ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'
            }`}>
            {s || 'All'}{s === '' && pagination.total != null ? ` (${pagination.total})` : ''}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Type</th>
                <th className="px-5 py-3 text-left font-medium">Device</th>
                <th className="px-5 py-3 text-left font-medium">Severity</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium">Message</th>
                <th className="px-5 py-3 text-left font-medium">Time</th>
                <th className="px-5 py-3 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-red-500">{error}</td></tr>
              )}
              {!loading && !error && alerts.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-secondary-400">
                  {statusFilter ? `No ${statusFilter} alerts` : 'No alerts found'}
                </td></tr>
              )}
              {!loading && alerts.map((a) => (
                <tr key={a.id} className="hover:bg-secondary-50 transition">
                  <td className="px-5 py-3 text-secondary-700 whitespace-nowrap">{a.alert_type.replace(/_/g, ' ')}</td>
                  <td className="px-5 py-3 text-primary-600 font-medium">{a.device_id}</td>
                  <td className="px-5 py-3"><SeverityBadge severity={a.severity} /></td>
                  <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-5 py-3 text-secondary-600 max-w-xs truncate">{a.message}</td>
                  <td className="px-5 py-3 text-secondary-500 text-xs whitespace-nowrap">{formatDate(a.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1.5">
                      {a.status === 'pending' && (
                        <button onClick={() => handleAcknowledge(a.id)}
                          className="px-2.5 py-1 text-xs bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition font-medium">
                          Ack
                        </button>
                      )}
                      {(a.status === 'pending' || a.status === 'acknowledged') && (
                        <button onClick={() => handleResolve(a.id)}
                          className="px-2.5 py-1 text-xs bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition font-medium">
                          Resolve
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-secondary-100 flex items-center justify-between">
            <p className="text-sm text-secondary-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
